/**
 * Guards against browsers that block Web Storage under strict privacy settings.
 *
 * Two distinct failure modes are handled for BOTH localStorage and sessionStorage:
 *   - Safari ("Block All Cookies") throws a SecurityError on ANY storage access,
 *     including just reading the window.localStorage / window.sessionStorage property.
 *   - Firefox (DOM storage disabled / site data blocked) leaves the property as null,
 *     so accessing a method throws a TypeError ("...reading 'getItem' on null").
 *
 * When blocked, native storage is replaced with an in-memory implementation so the app —
 * and every direct `localStorage.*` / `sessionStorage.*` call site — keeps working within
 * the page. Data does NOT survive a reload and is not shared across tabs, so a window flag
 * is set and surfaced via isStorageFallbackActive() to drive a "degraded storage" warning.
 */

const STORAGE_FALLBACK_FLAG = 'CMP_STORAGE_FALLBACK_ACTIVE';
const PROBE_KEY = '__cmp_storage_probe__';

type StorageName = 'localStorage' | 'sessionStorage';
type FlagWindow = Window & { [STORAGE_FALLBACK_FLAG]?: boolean };

/** Build a fresh in-memory, spec-compatible Storage backed by a Map. */
function createMemoryStorage(): Storage {
  const store = new Map<string, string>();
  return {
    getItem: (key: string) => (store.has(key) ? store.get(key)! : null),
    setItem: (key: string, value: string) => { store.set(key, String(value)); },
    removeItem: (key: string) => { store.delete(key); },
    clear: () => { store.clear(); },
    key: (index: number) => Array.from(store.keys())[index] ?? null,
    get length() { return store.size; },
  };
}

/** True when the named Web Storage can be read from and written to. */
function isStorageUsable(name: StorageName): boolean {
  try {
    const storage = window[name];
    if (!storage) {
      return false; // Firefox: property is null when DOM storage is disabled
    }
    storage.setItem(PROBE_KEY, '1');
    storage.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false; // Safari: SecurityError thrown on access
  }
}

/** Swap the named Web Storage for an in-memory fallback and flag the degraded state. */
function installMemoryFallback(name: StorageName): void {
  const memoryStorage = createMemoryStorage();
  try {
    Object.defineProperty(window, name, { value: memoryStorage, configurable: true });
  } catch {
    // Extremely rare: property is non-configurable. Nothing more we can safely do.
  }
  (window as FlagWindow)[STORAGE_FALLBACK_FLAG] = true;
}

/**
 * Ensure both localStorage and sessionStorage are usable, installing an in-memory
 * fallback for whichever is blocked. Call once from each portal's main.ts before
 * bootstrapModule().
 */
export function ensureWebStorage(): void {
  if (!isStorageUsable('localStorage')) {
    installMemoryFallback('localStorage');
  }
  if (!isStorageUsable('sessionStorage')) {
    installMemoryFallback('sessionStorage');
  }
}

/** True when an in-memory fallback is active (native storage was blocked at startup). */
export function isStorageFallbackActive(): boolean {
  return !!(window as FlagWindow)[STORAGE_FALLBACK_FLAG];
}
